import type { JSX } from "react";
import { ChevronLeft, CornerDownLeft } from "@/icons/lucide-compat";
import { cn } from "@openreel/ui";
import {
  FOCUS_RING,
  PILL_BASE,
  PILL_DARK,
  PILL_DIMMED,
  PILL_LIGHT,
  PILL_MOTION,
} from "./surface";

export interface StepNavProps {
  onBack: () => void;
  onContinue: () => void;
  /** Hidden entirely on steps that show a single centred Continue. */
  showBack?: boolean;
  /** Rendered but inert on the first step, where Back has nowhere to go. */
  backDisabled?: boolean;
  continueDisabled?: boolean;
  continueLabel?: string;
  /** A save is in flight; Continue is held until it settles. */
  busy?: boolean;
}

export function StepNav({
  onBack,
  onContinue,
  showBack = true,
  backDisabled = false,
  continueDisabled = false,
  continueLabel = "Continue",
  busy = false,
}: StepNavProps): JSX.Element {
  const disabled = continueDisabled || busy;

  return (
    <div className="flex items-center justify-center gap-3">
      {showBack ? (
        <button
          type="button"
          onClick={onBack}
          disabled={backDisabled}
          className={cn(
            PILL_BASE,
            FOCUS_RING,
            "pl-4",
            backDisabled
              ? "cursor-not-allowed border-white/5 bg-[#141414] text-white/25"
              : cn(PILL_MOTION, PILL_DARK),
          )}
        >
          <ChevronLeft size={16} strokeWidth={1.5} aria-hidden />
          <span>Back</span>
        </button>
      ) : null}

      <button
        type="button"
        onClick={onContinue}
        disabled={disabled}
        className={cn(
          PILL_BASE,
          FOCUS_RING,
          "pr-3",
          disabled ? PILL_DIMMED : cn(PILL_MOTION, PILL_LIGHT),
        )}
      >
        <span>{busy ? "Saving..." : continueLabel}</span>
        <span
          aria-hidden
          className={cn(
            "flex h-6 w-6 items-center justify-center rounded-md",
            disabled ? "bg-white/10 text-white/40" : "bg-black/10 text-[#0a0a0a]",
          )}
        >
          <CornerDownLeft size={13} strokeWidth={1.75} />
        </span>
      </button>
    </div>
  );
}
